import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { CheckCircle2, Circle, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useOnboardingProgress } from '@/hooks/useOnboardingProgress';
import { cn } from '@/lib/utils';

interface Step {
  id: string;
  label: string;
  description: string;
  field: 'profile_completed' | 'directory_explored' | 'first_message_sent' | 'first_opportunity_viewed';
  action: () => void;
}

const OnboardingStepper = () => {
  const navigate = useNavigate();
  const { member } = useAuth();
  const { progress, isLoading, isComplete, markStep } = useOnboardingProgress();

  if (!member || isLoading || isComplete) return null;

  // Auto-check profile completion from member data
  const profileDone = !!(member.image && member.bio && member.skills?.length > 0);
  if (profileDone && progress && !progress.profile_completed) {
    markStep('profile_completed');
  }

  const steps: Step[] = [
    {
      id: 'profile',
      label: 'Compléter votre profil',
      description: 'Photo, bio, compétences',
      field: 'profile_completed',
      action: () => navigate('/profile'),
    },
    {
      id: 'directory',
      label: "Explorer l'annuaire",
      description: 'Découvrez les autres membres',
      field: 'directory_explored',
      action: () => {
        markStep('directory_explored');
        navigate('/members');
      },
    },
    {
      id: 'networking',
      label: 'Envoyer un premier message',
      description: 'Connectez-vous avec un membre',
      field: 'first_message_sent',
      action: () => navigate('/networking?tab=hub'),
    },
    {
      id: 'opportunities',
      label: 'Consulter les opportunités',
      description: 'Trouvez des collaborations',
      field: 'first_opportunity_viewed',
      action: () => {
        markStep('first_opportunity_viewed');
        navigate('/opportunities');
      },
    },
  ];

  const isStepComplete = (step: Step): boolean => {
    if (step.field === 'profile_completed') return profileDone;
    return progress?.[step.field] ?? false;
  };

  const completedCount = steps.filter(isStepComplete).length;
  const progressPercent = (completedCount / steps.length) * 100;

  if (completedCount === steps.length) return null;

  return (
    <Card className="border-primary/20 bg-gradient-to-br from-primary/5 to-transparent">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between text-base">
          <span>🚀 Premiers pas</span>
          <span className="text-xs font-normal text-muted-foreground">
            {completedCount}/{steps.length} complétés
          </span>
        </CardTitle>
        <div className="w-full h-1.5 bg-muted rounded-full overflow-hidden mt-2">
          <div
            className="h-full bg-primary rounded-full transition-all duration-500"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {steps.map((step) => {
          const done = isStepComplete(step);
          return (
            <button
              key={step.id}
              onClick={step.action}
              className={cn(
                'flex items-center gap-3 w-full text-left p-2.5 rounded-lg transition-colors',
                done ? 'opacity-60' : 'hover:bg-accent/50 cursor-pointer'
              )}
            >
              {done ? (
                <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0" />
              ) : (
                <Circle className="h-5 w-5 text-muted-foreground flex-shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <div className={cn('text-sm font-medium', done && 'line-through')}>{step.label}</div>
                <div className="text-xs text-muted-foreground">{step.description}</div>
              </div>
              {!done && <ArrowRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />}
            </button>
          );
        })}
      </CardContent>
    </Card>
  );
};

export default OnboardingStepper;
